import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner, ToggleField } from '../../../shared/components';
import {
  checkEmailChannelAccountDns,
  createEmailChannelAccount,
  disconnectEmailOAuth,
  getEmailChannelAccountSettings,
  getEmailOAuthProviders,
  getEmailProviderCatalog,
  sendEmailChannelAccountTest,
  startEmailOAuth,
  testEmailChannelAccountInbound,
  testEmailChannelAccountOutbound,
  updateEmailChannelAccountCredentials,
} from '../services/channelAccountService';
import {
  CallingConnectionTestResult,
  ChannelAccountEmailSettings,
  EMAIL_INBOUND_WEBHOOK_PROVIDERS,
  EmailDnsCheckStatus,
  EmailDnsDiagnostics,
  EmailInboundMode,
  EmailOAuthProvider,
  EmailOAuthProvidersResponse,
  EmailOutboundProvider,
  EmailProviderCatalog,
  EmailReplyFormat,
  EmailSendResult,
  EmailTransportSecurity,
  UpdateEmailChannelAccountCredentialsRequest,
} from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import { ConnectorFieldStatus } from './ConnectorFieldStatus';
import { EmailIcon } from './emailProviderIcons';
import { ChipGroup, Field, StatusPill, Tone } from './emailFormParts';
import { EmailBrandingPanel } from './EmailBrandingPanel';
import { EmailLayoutSummary, listEmailLayouts } from '../services/emailLayoutService';
import styles from './EmailConnectorTab.module.css';

interface EmailSettingsForm {
  displayName: string;
  address: string;
  fromName: string;
  replyTo: string;
  bccCopyAddress: string;
  outboundProvider: EmailOutboundProvider;
  outboundEnabled: boolean;
  smtpHost: string;
  smtpPort: string;
  smtpSecurity: EmailTransportSecurity;
  smtpUsername: string;
  smtpPassword: string;
  smtpTimeoutSeconds: string;
  apiKey: string;
  apiBaseUrl: string;
  apiDomain: string;
  inboundMode: EmailInboundMode;
  inboundEnabled: boolean;
  imapHost: string;
  imapPort: string;
  imapSecurity: EmailTransportSecurity;
  imapUsername: string;
  imapPassword: string;
  imapFolder: string;
  imapProcessedFolder: string;
  imapPollIntervalSeconds: string;
  imapMarkAsRead: boolean;
  inboundWebhookSecret: string;
  replyFormat: EmailReplyFormat;
  subjectPrefix: string;
  defaultSubject: string;
  signature: string;
  quoteOriginal: boolean;
  threadIdleDays: string;
  maxAttachmentSizeMb: string;
  useBrandLayout: boolean;
  layoutId: string;
  useAgentSignatures: boolean;
  allowedSenderDomains: string;
  blockedSenderDomains: string;
  ignoreAutoGenerated: boolean;
  ignoreSubjectPattern: string;
  oauthProvider: EmailOAuthFormProvider;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthDirectoryTenant: string;
}

type EmailOAuthFormProvider = 'none' | EmailOAuthProvider;

/** Query-string flag the backend appends when the browser returns from the provider consent screen. */
export const EMAIL_OAUTH_RETURN_QUERY_KEY = 'emailOAuth';

const emptyEmailSettingsForm: EmailSettingsForm = {
  displayName: 'Email',
  address: '',
  fromName: '',
  replyTo: '',
  bccCopyAddress: '',
  outboundProvider: 'smtp',
  outboundEnabled: true,
  smtpHost: '',
  smtpPort: '587',
  smtpSecurity: 'starttls',
  smtpUsername: '',
  smtpPassword: '',
  smtpTimeoutSeconds: '30',
  apiKey: '',
  apiBaseUrl: '',
  apiDomain: '',
  inboundMode: 'none',
  inboundEnabled: false,
  imapHost: '',
  imapPort: '993',
  imapSecurity: 'ssl',
  imapUsername: '',
  imapPassword: '',
  imapFolder: 'INBOX',
  imapProcessedFolder: '',
  imapPollIntervalSeconds: '60',
  imapMarkAsRead: true,
  inboundWebhookSecret: '',
  replyFormat: 'html',
  subjectPrefix: 'Re:',
  defaultSubject: '',
  signature: '',
  quoteOriginal: false,
  threadIdleDays: '30',
  maxAttachmentSizeMb: '10',
  useBrandLayout: true,
  layoutId: '',
  useAgentSignatures: true,
  allowedSenderDomains: '',
  blockedSenderDomains: '',
  ignoreAutoGenerated: true,
  ignoreSubjectPattern: '',
  oauthProvider: 'none',
  oauthClientId: '',
  oauthClientSecret: '',
  oauthDirectoryTenant: '',
};

const formFromSettings = (data: ChannelAccountEmailSettings): EmailSettingsForm => ({
  displayName: 'Email',
  address: data.address,
  fromName: data.fromName ?? '',
  replyTo: data.replyTo ?? '',
  bccCopyAddress: data.bccCopyAddress ?? '',
  outboundProvider: data.outboundProvider,
  outboundEnabled: data.outboundEnabled,
  smtpHost: data.smtp.host ?? '',
  smtpPort: String(data.smtp.port),
  smtpSecurity: data.smtp.security,
  smtpUsername: data.smtp.username ?? '',
  smtpPassword: '',
  smtpTimeoutSeconds: String(data.smtp.timeoutSeconds),
  apiKey: '',
  apiBaseUrl: data.api.baseUrl ?? '',
  apiDomain: data.api.domain ?? '',
  inboundMode: data.inboundMode,
  inboundEnabled: data.inboundEnabled,
  imapHost: data.imap.host ?? '',
  imapPort: String(data.imap.port),
  imapSecurity: data.imap.security,
  imapUsername: data.imap.username ?? '',
  imapPassword: '',
  imapFolder: data.imap.folder,
  imapProcessedFolder: data.imap.processedFolder ?? '',
  imapPollIntervalSeconds: String(data.imap.pollIntervalSeconds),
  imapMarkAsRead: data.imap.markAsRead,
  inboundWebhookSecret: '',
  replyFormat: data.behaviour.replyFormat,
  subjectPrefix: data.behaviour.subjectPrefix ?? '',
  defaultSubject: data.behaviour.defaultSubject ?? '',
  signature: data.behaviour.signature ?? '',
  quoteOriginal: data.behaviour.quoteOriginal,
  threadIdleDays: String(data.behaviour.threadIdleDays),
  maxAttachmentSizeMb: String(data.behaviour.maxAttachmentSizeMb),
  useBrandLayout: data.behaviour.useBrandLayout,
  layoutId: data.behaviour.layoutId ?? '',
  useAgentSignatures: data.behaviour.useAgentSignatures !== false,
  allowedSenderDomains: data.filters.allowedSenderDomains.join(', '),
  blockedSenderDomains: data.filters.blockedSenderDomains.join(', '),
  ignoreAutoGenerated: data.filters.ignoreAutoGenerated,
  ignoreSubjectPattern: data.filters.ignoreSubjectPattern ?? '',
  oauthProvider: data.oauth?.provider ?? 'none',
  oauthClientId: data.oauth?.clientId ?? '',
  oauthClientSecret: '',
  oauthDirectoryTenant: data.oauth?.directoryTenant ?? '',
});

const parseNumber = (value: string, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const splitDomains = (value: string): string[] =>
  value
    .split(/[,\s;]+/)
    .map(item => item.trim().toLowerCase())
    .filter(Boolean);

/**
 * Builds the PUT payload. Text fields always travel (an empty string clears them on the server);
 * secrets travel only when typed, so a blank password box keeps whatever is stored.
 */
const buildUpdateRequest = (form: EmailSettingsForm): UpdateEmailChannelAccountCredentialsRequest => ({
  address: form.address.trim() || undefined,
  fromName: form.fromName,
  replyTo: form.replyTo,
  bccCopyAddress: form.bccCopyAddress,
  outboundProvider: form.outboundProvider,
  outboundEnabled: form.outboundEnabled,
  smtp: {
    host: form.smtpHost,
    port: parseNumber(form.smtpPort, 587),
    security: form.smtpSecurity,
    username: form.smtpUsername,
    password: form.smtpPassword.trim() || undefined,
    timeoutSeconds: parseNumber(form.smtpTimeoutSeconds, 30),
  },
  api: {
    apiKey: form.apiKey.trim() || undefined,
    baseUrl: form.apiBaseUrl,
    domain: form.apiDomain,
  },
  inboundMode: form.inboundMode,
  inboundEnabled: form.inboundEnabled,
  imap: {
    host: form.imapHost,
    port: parseNumber(form.imapPort, 993),
    security: form.imapSecurity,
    username: form.imapUsername,
    password: form.imapPassword.trim() || undefined,
    folder: form.imapFolder,
    processedFolder: form.imapProcessedFolder,
    pollIntervalSeconds: parseNumber(form.imapPollIntervalSeconds, 60),
    markAsRead: form.imapMarkAsRead,
  },
  inboundWebhookSecret: form.inboundWebhookSecret.trim() || undefined,
  behaviour: {
    replyFormat: form.replyFormat,
    subjectPrefix: form.subjectPrefix,
    defaultSubject: form.defaultSubject,
    signature: form.signature,
    quoteOriginal: form.quoteOriginal,
    threadIdleDays: parseNumber(form.threadIdleDays, 30),
    maxAttachmentSizeMb: parseNumber(form.maxAttachmentSizeMb, 10),
    useBrandLayout: form.useBrandLayout,
    layoutId: form.layoutId,
    useAgentSignatures: form.useAgentSignatures,
  },
  filters: {
    allowedSenderDomains: splitDomains(form.allowedSenderDomains),
    blockedSenderDomains: splitDomains(form.blockedSenderDomains),
    ignoreAutoGenerated: form.ignoreAutoGenerated,
    ignoreSubjectPattern: form.ignoreSubjectPattern,
  },
  oauth: {
    provider: form.oauthProvider,
    clientId: form.oauthClientId,
    clientSecret: form.oauthClientSecret.trim() || undefined,
    directoryTenant: form.oauthDirectoryTenant,
  },
});

const formatUtcTimestamp = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/** Public URL a provider must POST to; mirrors the other connectors' webhook-URL derivation. */
export const buildEmailInboundWebhookUrl = (provider: string, channelAccountId: string): string => {
  const apiBase = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/\/$/, '');
  return `${apiBase}/api/v1/email/webhook/${provider}/${channelAccountId}`;
};

const dotClass: Record<Tone, string> = {
  ok: styles.toneOk,
  warn: styles.toneWarn,
  bad: styles.toneBad,
  off: styles.toneOff,
};

const dnsTone: Record<EmailDnsCheckStatus, Tone> = {
  pass: 'ok',
  warn: 'warn',
  fail: 'bad',
  unknown: 'off',
};

type SectionId = 'identity' | 'outbound' | 'oauth' | 'inbound' | 'behaviour' | 'filters' | 'tests';

const defaultOpenSections: Record<SectionId, boolean> = {
  identity: true,
  outbound: true,
  oauth: false,
  inbound: true,
  behaviour: false,
  filters: false,
  tests: true,
};

interface EmailConnectorTabProps {
  canManage: boolean;
}

export const EmailConnectorTab: React.FC<EmailConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const emailChannelAccounts = useChannelAccounts('Email', true);

  const [catalog, setCatalog] = React.useState<EmailProviderCatalog | null>(null);
  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [testingOutbound, setTestingOutbound] = React.useState(false);
  const [testingInbound, setTestingInbound] = React.useState(false);
  const [sendingTest, setSendingTest] = React.useState(false);
  const [accountSettings, setAccountSettings] = React.useState<ChannelAccountEmailSettings | null>(null);
  const [settingsForm, setSettingsForm] = React.useState<EmailSettingsForm>(emptyEmailSettingsForm);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);
  const [settingsNotice, setSettingsNotice] = React.useState<string | null>(null);
  const [latestOutboundTest, setLatestOutboundTest] = React.useState<CallingConnectionTestResult | null>(null);
  const [latestInboundTest, setLatestInboundTest] = React.useState<CallingConnectionTestResult | null>(null);
  const [latestSendTest, setLatestSendTest] = React.useState<EmailSendResult | null>(null);
  const [showAddAccount, setShowAddAccount] = React.useState(false);
  const [testRecipient, setTestRecipient] = React.useState('');
  const [selectedPreset, setSelectedPreset] = React.useState('');
  const [oauthProviders, setOAuthProviders] = React.useState<EmailOAuthProvidersResponse | null>(null);
  const [startingOAuth, setStartingOAuth] = React.useState(false);
  const [disconnectingOAuth, setDisconnectingOAuth] = React.useState(false);
  const [checkingDns, setCheckingDns] = React.useState(false);
  const [dkimSelector, setDkimSelector] = React.useState('');
  const [dnsDiagnostics, setDnsDiagnostics] = React.useState<EmailDnsDiagnostics | null>(null);
  const [openSections, setOpenSections] = React.useState<Record<SectionId, boolean>>(defaultOpenSections);
  const sectionRefs = React.useRef<Partial<Record<SectionId, HTMLDivElement | null>>>({});

  React.useEffect(() => {
    let cancelled = false;
    getEmailProviderCatalog()
      .then(result => {
        if (!cancelled) {
          setCatalog(result);
        }
      })
      .catch(() => {
        // The form still works without the catalog; it just loses presets and provider labels.
        if (!cancelled) {
          setCatalog(null);
        }
      });
    getEmailOAuthProviders()
      .then(result => {
        if (!cancelled) {
          setOAuthProviders(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOAuthProviders(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [layouts, setLayouts] = React.useState<EmailLayoutSummary[]>([]);
  const refreshLayouts = React.useCallback(() => {
    listEmailLayouts()
      .then(result => setLayouts(result.layouts))
      // Without the list the picker only offers the workspace default, which is still a valid choice.
      .catch(() => setLayouts([]));
  }, []);
  React.useEffect(() => {
    refreshLayouts();
  }, [refreshLayouts]);

  // Returning from the provider consent screen: the backend redirects to ?tab=email&accountId=…&emailOAuth=<outcome>.
  const { setSelectedAccountId } = emailChannelAccounts;
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get(EMAIL_OAUTH_RETURN_QUERY_KEY);
    if (!outcome) {
      return;
    }

    const accountId = params.get('accountId');
    const url = new URL(window.location.href);
    url.searchParams.delete(EMAIL_OAUTH_RETURN_QUERY_KEY);
    url.searchParams.delete('accountId');
    window.history.replaceState({}, '', url.toString());

    if (accountId) {
      setSelectedAccountId(accountId);
    }
    setOpenSections(prev => ({ ...prev, oauth: true }));

    if (outcome === 'success') {
      showToast(t('emailOAuthSuccess'), 'success');
      return;
    }

    const reasonKey = `emailOAuthFailed_${outcome}`;
    const reason = t(reasonKey);
    showToast(reason === reasonKey ? t('emailOAuthFailed') : reason, 'error');
  }, [setSelectedAccountId, showToast, t]);

  const loadAccountSettings = React.useCallback(async (accountId: string) => {
    setLoadingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const data = await getEmailChannelAccountSettings(accountId);
      setAccountSettings(data);
      setLatestOutboundTest(null);
      setLatestInboundTest(null);
      setLatestSendTest(null);
      setDnsDiagnostics(null);
      setSettingsForm(formFromSettings(data));
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : t('failedToLoadEmailSettings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [t]);

  React.useEffect(() => {
    const accountId = emailChannelAccounts.selectedAccountId;
    if (!accountId) {
      setAccountSettings(null);
      setSettingsForm(emptyEmailSettingsForm);
      return;
    }

    void loadAccountSettings(accountId);
  }, [loadAccountSettings, emailChannelAccounts.selectedAccountId]);

  const isInitialSetup = emailChannelAccounts.accounts.length === 0;
  const selectedProvider = catalog?.providers.find(provider => provider.id === settingsForm.outboundProvider) ?? null;
  const usesApiProvider = settingsForm.outboundProvider !== 'smtp';
  const providerOptions = catalog?.providers.length
    ? catalog.providers
    : [
      { id: 'smtp', label: 'SMTP', kind: 'smtp' as const, requiresDomain: false },
      { id: 'sendgrid', label: 'SendGrid', kind: 'api' as const, requiresDomain: false },
      { id: 'mailgun', label: 'Mailgun', kind: 'api' as const, requiresDomain: true },
      { id: 'postmark', label: 'Postmark', kind: 'api' as const, requiresDomain: false },
      { id: 'resend', label: 'Resend', kind: 'api' as const, requiresDomain: false },
    ];
  const oauthProviderOptions = oauthProviders?.providers ?? [
    { id: 'google' as const, label: 'Google Workspace / Gmail', platformAppConfigured: false, scopes: [] },
    { id: 'microsoft' as const, label: 'Microsoft 365 / Outlook', platformAppConfigured: false, scopes: [] },
  ];
  const showOAuthSection = !usesApiProvider || settingsForm.inboundMode === 'imap';

  const baselineForm = React.useMemo(
    () => (accountSettings ? formFromSettings(accountSettings) : emptyEmailSettingsForm),
    [accountSettings],
  );
  const isDirty = JSON.stringify(settingsForm) !== JSON.stringify(baselineForm);

  const applyPreset = (presetId: string) => {
    setSelectedPreset(presetId);
    const preset = catalog?.presets.find(item => item.id === presetId);
    if (!preset) {
      return;
    }

    setSettingsForm(prev => ({
      ...prev,
      smtpHost: preset.smtpHost ?? prev.smtpHost,
      smtpPort: preset.smtpPort != null ? String(preset.smtpPort) : prev.smtpPort,
      smtpSecurity: (preset.smtpSecurity as EmailTransportSecurity | null) ?? prev.smtpSecurity,
      imapHost: preset.imapHost ?? prev.imapHost,
      imapPort: preset.imapPort != null ? String(preset.imapPort) : prev.imapPort,
      imapSecurity: (preset.imapSecurity as EmailTransportSecurity | null) ?? prev.imapSecurity,
    }));
  };

  const changeProvider = (providerId: string) => {
    const provider = catalog?.providers.find(item => item.id === providerId);
    setSettingsForm(prev => ({
      ...prev,
      outboundProvider: providerId as EmailOutboundProvider,
      // A stale base URL from the previous provider would silently break sends.
      apiBaseUrl: provider?.defaultBaseUrl ?? '',
      apiKey: '',
    }));
  };

  const saveSettings = async () => {
    const selectedAccount = emailChannelAccounts.selectedAccount;
    if (!selectedAccount && !isInitialSetup) {
      return;
    }

    if (!settingsForm.address.trim()) {
      const message = t('emailAddressRequired');
      setSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setSavingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const request = buildUpdateRequest(settingsForm);
      if (isInitialSetup) {
        await createEmailChannelAccount({
          ...request,
          address: settingsForm.address.trim(),
          displayName: settingsForm.displayName.trim() || settingsForm.address.trim(),
          isDefault: true,
        });
        await emailChannelAccounts.reload();
      } else if (selectedAccount) {
        await updateEmailChannelAccountCredentials(selectedAccount.id, request);
        await loadAccountSettings(selectedAccount.id);
        await emailChannelAccounts.reload();
      }

      setSettingsForm(prev => ({ ...prev, smtpPassword: '', apiKey: '', imapPassword: '', inboundWebhookSecret: '', oauthClientSecret: '' }));
      setSettingsNotice(t('emailSettingsSaved'));
      showToast(t('emailSettingsSaved'), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSaveEmailSettings');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const runTest = async (kind: 'outbound' | 'inbound') => {
    const selectedAccount = emailChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    const setBusy = kind === 'outbound' ? setTestingOutbound : setTestingInbound;
    const setResult = kind === 'outbound' ? setLatestOutboundTest : setLatestInboundTest;
    const fallbackFailure = kind === 'outbound' ? t('failedToTestEmailOutbound') : t('failedToTestEmailInbound');
    const successText = kind === 'outbound' ? t('emailOutboundTestSucceeded') : t('emailInboundTestSucceeded');

    setBusy(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = kind === 'outbound'
        ? await testEmailChannelAccountOutbound(selectedAccount.id)
        : await testEmailChannelAccountInbound(selectedAccount.id);
      setResult(result);
      if (result.succeeded) {
        setSettingsNotice(result.message || successText);
        showToast(result.message || successText, 'success');
      } else {
        const message = result.message || fallbackFailure;
        setSettingsError(message);
        showToast(message, 'error');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : fallbackFailure;
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const sendTestEmail = async () => {
    const selectedAccount = emailChannelAccounts.selectedAccount;
    if (!selectedAccount || !testRecipient.trim()) {
      return;
    }

    setSendingTest(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await sendEmailChannelAccountTest(selectedAccount.id, testRecipient.trim());
      setLatestSendTest(result);
      if (result.succeeded) {
        setSettingsNotice(t('emailTestSendSucceeded', { to: testRecipient.trim() }));
        showToast(t('emailTestSendSucceeded', { to: testRecipient.trim() }), 'success');
      } else {
        const message = result.failureReason || t('failedToSendTestEmail');
        setSettingsError(message);
        showToast(message, 'error');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSendTestEmail');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const connectOAuth = async () => {
    const selectedAccount = emailChannelAccounts.selectedAccount;
    if (!selectedAccount || settingsForm.oauthProvider === 'none') {
      return;
    }

    // The consent URL is built from the saved provider and client app; an unsaved change would sign in with the wrong one.
    const savedProvider = accountSettings?.oauth.provider ?? 'none';
    const dirtyOwnApp = settingsForm.oauthClientSecret.trim().length > 0
      || settingsForm.oauthClientId.trim() !== (accountSettings?.oauth.clientId ?? '')
      || settingsForm.oauthDirectoryTenant.trim() !== (accountSettings?.oauth.directoryTenant ?? '');
    if (savedProvider !== settingsForm.oauthProvider || dirtyOwnApp) {
      const message = t('emailOAuthSaveFirst');
      setSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setStartingOAuth(true);
    setSettingsError(null);
    try {
      const result = await startEmailOAuth(selectedAccount.id, settingsForm.oauthProvider);
      window.location.assign(result.authorizeUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('emailOAuthFailed');
      setSettingsError(message);
      showToast(message, 'error');
      setStartingOAuth(false);
    }
  };

  const disconnectOAuth = async () => {
    const selectedAccount = emailChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setDisconnectingOAuth(true);
    setSettingsError(null);
    try {
      const data = await disconnectEmailOAuth(selectedAccount.id);
      setAccountSettings(data);
      showToast(t('emailOAuthDisconnected'), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('emailOAuthFailed');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setDisconnectingOAuth(false);
    }
  };

  const runDnsCheck = async () => {
    const selectedAccount = emailChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    setCheckingDns(true);
    setSettingsError(null);
    try {
      setDnsDiagnostics(await checkEmailChannelAccountDns(selectedAccount.id, dkimSelector));
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToCheckEmailDns');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setCheckingDns(false);
    }
  };

  const copyToClipboard = (value: string, toastKey: string) => {
    void navigator.clipboard?.writeText(value).then(() => showToast(t(toastKey), 'success'));
  };

  const toggleSection = (id: SectionId) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  const setAllSections = (open: boolean) => setOpenSections({
    identity: open, outbound: open, oauth: open, inbound: open, behaviour: open, filters: open, tests: open,
  });
  const revealSection = (id: SectionId) => {
    setOpenSections(prev => ({ ...prev, [id]: true }));
    // Deferred so the body has rendered before we scroll; jsdom has no scrollIntoView.
    window.setTimeout(() => sectionRefs.current[id]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  const update = <K extends keyof EmailSettingsForm>(key: K) => (value: EmailSettingsForm[K]) =>
    setSettingsForm(prev => ({ ...prev, [key]: value }));

  const textField = (
    key: keyof EmailSettingsForm,
    label: string,
    options?: { placeholder?: string; type?: string; disabled?: boolean; hint?: string; wide?: boolean; autoComplete?: string },
  ) => (
    <Field label={label} hint={options?.hint} wide={options?.wide}>
      <input
        className="text-input"
        type={options?.type ?? 'text'}
        value={String(settingsForm[key] ?? '')}
        onChange={event => setSettingsForm(prev => ({ ...prev, [key]: event.target.value }))}
        placeholder={options?.placeholder}
        disabled={!canManage || options?.disabled}
        autoComplete={options?.autoComplete ?? (options?.type === 'password' ? 'new-password' : 'off')}
      />
    </Field>
  );

  const securitySelect = (key: 'smtpSecurity' | 'imapSecurity', label: string) => (
    <Field label={label}>
      <select
        className="text-input"
        value={settingsForm[key]}
        onChange={event => setSettingsForm(prev => ({ ...prev, [key]: event.target.value as EmailTransportSecurity }))}
        disabled={!canManage}
      >
        <option value="starttls">{t('emailSecurityStartTls')}</option>
        <option value="ssl">{t('emailSecuritySsl')}</option>
        <option value="none">{t('emailSecurityNone')}</option>
      </select>
    </Field>
  );

  const renderTestResult = (result: CallingConnectionTestResult | null) => (
    result ? (
      <p className={`${styles.testResult} ${result.succeeded ? styles.testOk : styles.testBad}`}>
        {result.message}
      </p>
    ) : null
  );

  // ---- Derived status for the summary strip and card pills ----

  const outboundTone: Tone = !settingsForm.outboundEnabled
    ? 'off'
    : accountSettings?.isOutboundConfigured ? 'ok' : 'bad';
  const outboundLabel = providerOptions.find(provider => provider.id === settingsForm.outboundProvider)?.label ?? settingsForm.outboundProvider;

  const inboundTone: Tone = settingsForm.inboundMode === 'none' || !settingsForm.inboundEnabled
    ? 'off'
    : accountSettings?.inboundState?.lastError
      ? 'warn'
      : accountSettings?.isInboundConfigured ? 'ok' : 'bad';
  const inboundLabel = settingsForm.inboundMode === 'imap'
    ? t('emailInboundModeImap')
    : settingsForm.inboundMode === 'webhook'
      ? t('emailInboundModeWebhook')
      : t('emailSummaryOff');

  const oauthConnected = Boolean(accountSettings?.oauth.isConnected);
  const oauthTone: Tone = settingsForm.oauthProvider === 'none'
    ? 'off'
    : oauthConnected ? 'ok' : 'warn';
  const oauthLabel = settingsForm.oauthProvider === 'none'
    ? t('emailSummaryPassword')
    : oauthConnected
      ? (accountSettings?.oauth.account ?? oauthProviderOptions.find(provider => provider.id === settingsForm.oauthProvider)?.label ?? settingsForm.oauthProvider)
      : t('emailOAuthNotConnectedHeading');

  const dnsTone_: Tone = dnsDiagnostics ? dnsTone[dnsDiagnostics.overallStatus] : 'off';
  const dnsLabel = dnsDiagnostics ? t(`emailDnsStatus_${dnsDiagnostics.overallStatus}`) : t('emailSummaryNotChecked');

  const summaryItems: { id: SectionId; icon: string; label: string; value: string; tone: Tone }[] = [
    { id: 'outbound', icon: settingsForm.outboundProvider, label: t('emailSummaryOutbound'), value: outboundLabel, tone: outboundTone },
    { id: 'inbound', icon: settingsForm.inboundMode === 'none' ? 'none' : settingsForm.inboundMode, label: t('emailSummaryInbound'), value: inboundLabel, tone: inboundTone },
    { id: 'oauth', icon: settingsForm.oauthProvider === 'none' ? 'password' : settingsForm.oauthProvider, label: t('emailSummarySignIn'), value: oauthLabel, tone: oauthTone },
    { id: 'tests', icon: 'dns', label: t('emailSummaryDns'), value: dnsLabel, tone: dnsTone_ },
  ];

  // ---- Card shell ----

  let stepNumber = 0;
  const renderSection = (
    id: SectionId,
    title: string,
    help: string,
    pill: React.ReactNode,
    body: React.ReactNode,
  ) => {
    stepNumber += 1;
    const open = openSections[id];
    const bodyId = `email-section-${id}`;
    return (
      <div
        key={id}
        className={styles.step}
        ref={element => { sectionRefs.current[id] = element; }}
        data-testid={`email-section-${id}`}
      >
        <button
          type="button"
          className={styles.stepHead}
          onClick={() => toggleSection(id)}
          aria-expanded={open}
          aria-controls={bodyId}
        >
          <span className={styles.stepTitle}>
            <span className={styles.stepBadge}>{stepNumber}</span>
            <h3>{title}</h3>
          </span>
          <span className={styles.stepMeta}>
            {pill}
            <svg
              className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 8l5 5 5-5" />
            </svg>
          </span>
        </button>
        {open ? (
          <div id={bodyId} className={styles.stepBody}>
            <p className={styles.stepHelp}>{help}</p>
            {body}
          </div>
        ) : null}
      </div>
    );
  };

  // ---- Section bodies ----

  const identityBody = (
    <div className="company-form-grid">
      {isInitialSetup ? textField('displayName', t('emailDisplayName'), { placeholder: 'Support mailbox' }) : null}
      {textField('address', t('emailAddress'), { placeholder: 'support@company.com', type: 'email' })}
      {textField('fromName', t('emailFromName'), { placeholder: 'Company Support' })}
      {textField('replyTo', t('emailReplyTo'), { placeholder: 'replies@company.com', type: 'email' })}
      {textField('bccCopyAddress', t('emailBccCopy'), { placeholder: 'archive@company.com', type: 'email', hint: t('emailBccCopyHint') })}
    </div>
  );

  const outboundBody = (
    <div className="company-form-grid">
      <ChipGroup
        ariaLabel={t('emailOutboundProvider')}
        testId="email-outbound-provider"
        value={settingsForm.outboundProvider}
        options={providerOptions.map(provider => ({ id: provider.id as EmailOutboundProvider, label: provider.label, icon: provider.id }))}
        onChange={changeProvider}
        disabled={!canManage}
      />
      {selectedProvider?.docsUrl ? (
        <span className={`${styles.fieldWide} company-field-hint`}>
          <a href={selectedProvider.docsUrl} target="_blank" rel="noreferrer">{t('emailProviderDocs')}</a>
        </span>
      ) : null}
      <ToggleField
        label={t('emailOutboundEnabled')}
        checked={settingsForm.outboundEnabled}
        onChange={update('outboundEnabled')}
        disabled={!canManage}
      />

      {!usesApiProvider ? (
        <>
          {catalog?.presets.length ? (
            <>
              <div className={styles.subHeading}>{t('emailMailboxPreset')}</div>
              <ChipGroup
                ariaLabel={t('emailMailboxPreset')}
                testId="email-mailbox-preset"
                value={selectedPreset}
                options={[
                  { id: '', label: t('emailMailboxPresetNone'), icon: 'none' },
                  ...catalog.presets.map(preset => ({ id: preset.id, label: preset.label, icon: preset.id })),
                ]}
                onChange={applyPreset}
                disabled={!canManage}
              />
              {catalog.presets.find(preset => preset.id === selectedPreset)?.note ? (
                <span className={`${styles.fieldWide} company-field-hint`}>{catalog.presets.find(preset => preset.id === selectedPreset)?.note}</span>
              ) : null}
            </>
          ) : null}
          {textField('smtpHost', t('emailSmtpHost'), { placeholder: 'smtp.example.com' })}
          {textField('smtpPort', t('emailSmtpPort'), { placeholder: '587', type: 'number' })}
          {securitySelect('smtpSecurity', t('emailSmtpSecurity'))}
          {textField('smtpTimeoutSeconds', t('emailSmtpTimeout'), { type: 'number', placeholder: '30' })}
          {textField('smtpUsername', t('emailSmtpUsername'), { placeholder: settingsForm.address || 'user@example.com', hint: t('emailUsernameDefaultsToAddress') })}
          {textField('smtpPassword', t('emailSmtpPassword'), {
            type: 'password',
            placeholder: accountSettings?.smtp.hasPassword ? t('emailCredentialConfigured') : '',
            hint: oauthConnected ? t('emailOAuthPasswordNotNeeded') : undefined,
          })}
        </>
      ) : (
        <>
          {textField('apiKey', t('emailApiKey'), { type: 'password', placeholder: accountSettings?.api.hasApiKey ? t('emailCredentialConfigured') : '' })}
          {textField('apiBaseUrl', t('emailApiBaseUrl'), { placeholder: selectedProvider?.defaultBaseUrl ?? 'https://api.provider.com', hint: t('emailApiBaseUrlHint') })}
          {selectedProvider?.requiresDomain || settingsForm.outboundProvider === 'mailgun'
            ? textField('apiDomain', t('emailApiDomain'), { placeholder: 'mg.company.com', hint: t('emailApiDomainHint') })
            : null}
        </>
      )}

      <div className={`${styles.fieldWide} ${styles.credentialStatus}`}>
        {usesApiProvider
          ? <span>{t('emailApiKey')}: <ConnectorFieldStatus done={Boolean(accountSettings?.api.hasApiKey)} /></span>
          : <span>{t('emailSmtpPassword')}: <ConnectorFieldStatus done={Boolean(accountSettings?.smtp.hasPassword)} /></span>}
        <span>{t('emailOutboundStatus')}: <ConnectorFieldStatus done={Boolean(accountSettings?.isOutboundConfigured)} /></span>
      </div>
    </div>
  );

  const oauthBody = (
    <div className="company-form-grid">
      <ChipGroup
        ariaLabel={t('emailOAuthProvider')}
        testId="email-oauth-provider"
        value={settingsForm.oauthProvider}
        options={[
          { id: 'none' as EmailOAuthFormProvider, label: t('emailOAuthProviderNone'), icon: 'password' },
          ...oauthProviderOptions.map(provider => ({ id: provider.id as EmailOAuthFormProvider, label: provider.label, icon: provider.id })),
        ]}
        onChange={value => setSettingsForm(prev => ({ ...prev, oauthProvider: value }))}
        disabled={!canManage}
      />
      <span className={`${styles.fieldWide} company-field-hint`}>{t('emailOAuthProviderHint')}</span>

      {settingsForm.oauthProvider !== 'none' ? (
        <>
          <div className={styles.panel} data-testid="email-oauth-status">
            {oauthConnected ? (
              <>
                <span className={styles.panelTitle}>
                  <EmailIcon id={settingsForm.oauthProvider} size={18} />
                  {t('emailOAuthConnectedHeading')}
                </span>
                <span>{t('emailOAuthConnectedAs', { account: accountSettings?.oauth.account ?? settingsForm.address })}</span>
                {accountSettings?.oauth.connectedAtUtc ? (
                  <span>{t('emailOAuthConnectedAt', { value: formatUtcTimestamp(accountSettings.oauth.connectedAtUtc) })}</span>
                ) : null}
              </>
            ) : (
              <>
                <span className={styles.panelTitle}>
                  <EmailIcon id={settingsForm.oauthProvider} size={18} />
                  {t('emailOAuthNotConnectedHeading')}
                </span>
                <span>
                  {oauthProviderOptions.find(provider => provider.id === settingsForm.oauthProvider)?.platformAppConfigured
                    ? t('emailOAuthPlatformAppReady')
                    : t('emailOAuthOwnAppRequired')}
                </span>
              </>
            )}
            {canManage ? (
              <div className={styles.inlineActions}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { void connectOAuth(); }}
                  disabled={startingOAuth || !emailChannelAccounts.selectedAccount}
                >
                  {startingOAuth
                    ? t('emailOAuthRedirecting')
                    : oauthConnected
                      ? t('emailOAuthReconnect')
                      : t('emailOAuthConnect', { provider: settingsForm.oauthProvider === 'google' ? 'Google' : 'Microsoft' })}
                </button>
                {oauthConnected ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { void disconnectOAuth(); }}
                    disabled={disconnectingOAuth}
                  >
                    {disconnectingOAuth ? t('savingEllipsis', { ns: 'admin' }) : t('emailOAuthDisconnect')}
                  </button>
                ) : null}
              </div>
            ) : null}
            {!emailChannelAccounts.selectedAccount ? <span>{t('emailOAuthSaveAccountFirst')}</span> : null}
          </div>

          <div className={styles.subHeading}>{t('emailOAuthOwnAppHeading')}</div>
          {textField('oauthClientId', t('emailOAuthClientId'), { placeholder: t('emailOAuthClientIdPlaceholder'), hint: t('emailOAuthOwnAppHint') })}
          {textField('oauthClientSecret', t('emailOAuthClientSecret'), { type: 'password', placeholder: accountSettings?.oauth.hasClientSecret ? t('emailCredentialConfigured') : '' })}
          {settingsForm.oauthProvider === 'microsoft'
            ? textField('oauthDirectoryTenant', t('emailOAuthDirectoryTenant'), { placeholder: 'common', hint: t('emailOAuthDirectoryTenantHint') })
            : null}
          {oauthProviders?.redirectUri ? (
            <div className={styles.panel} data-testid="email-oauth-redirect-uri">
              <span className={styles.panelTitle}>{t('emailOAuthRedirectUriHeading')}</span>
              <span>{t('emailOAuthRedirectUriHelp')}</span>
              <div className={styles.copyRow}>
                <code className={styles.code}>{oauthProviders.redirectUri}</code>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(oauthProviders.redirectUri, 'emailOAuthRedirectUriCopied')}
                >
                  {t('emailInboundWebhookUrlCopy')}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );

  const inboundBody = (
    <div className="company-form-grid">
      <ChipGroup
        ariaLabel={t('emailInboundMode')}
        testId="email-inbound-mode"
        value={settingsForm.inboundMode}
        options={[
          { id: 'none' as EmailInboundMode, label: t('emailInboundModeNone'), icon: 'none' },
          { id: 'imap' as EmailInboundMode, label: t('emailInboundModeImap'), icon: 'imap' },
          { id: 'webhook' as EmailInboundMode, label: t('emailInboundModeWebhook'), icon: 'webhook' },
        ]}
        onChange={value => setSettingsForm(prev => ({ ...prev, inboundMode: value }))}
        disabled={!canManage}
      />
      <span className={`${styles.fieldWide} company-field-hint`}>{t('emailInboundModeHint')}</span>
      {settingsForm.inboundMode !== 'none' ? (
        <ToggleField
          label={t('emailInboundEnabled')}
          checked={settingsForm.inboundEnabled}
          onChange={update('inboundEnabled')}
          disabled={!canManage}
        />
      ) : null}

      {settingsForm.inboundMode === 'imap' ? (
        <>
          {textField('imapHost', t('emailImapHost'), { placeholder: 'imap.example.com' })}
          {textField('imapPort', t('emailImapPort'), { placeholder: '993', type: 'number' })}
          {securitySelect('imapSecurity', t('emailImapSecurity'))}
          {textField('imapPollIntervalSeconds', t('emailImapPollInterval'), { type: 'number', placeholder: '60' })}
          {textField('imapUsername', t('emailImapUsername'), { placeholder: settingsForm.address || 'user@example.com', hint: t('emailUsernameDefaultsToAddress') })}
          {textField('imapPassword', t('emailImapPassword'), {
            type: 'password',
            placeholder: accountSettings?.imap.hasPassword ? t('emailCredentialConfigured') : '',
            hint: oauthConnected ? t('emailOAuthPasswordNotNeeded') : undefined,
          })}
          {textField('imapFolder', t('emailImapFolder'), { placeholder: 'INBOX' })}
          {textField('imapProcessedFolder', t('emailImapProcessedFolder'), { placeholder: 'Processed', hint: t('emailImapProcessedFolderHint') })}
          <ToggleField
            label={t('emailImapMarkAsRead')}
            checked={settingsForm.imapMarkAsRead}
            onChange={update('imapMarkAsRead')}
            disabled={!canManage}
          />
          {accountSettings?.inboundMode === 'imap' ? (
            <div className={styles.panel} data-testid="email-inbound-state">
              <span className={styles.panelTitle}>{t('emailInboundStateHeading')}</span>
              {accountSettings.inboundState?.lastPolledAtUtc ? (
                <>
                  <span>{t('emailInboundStateLastPolled', { value: formatUtcTimestamp(accountSettings.inboundState.lastPolledAtUtc) })}</span>
                  <span>
                    {accountSettings.inboundState.lastImportedAtUtc
                      ? t('emailInboundStateLastImported', { value: formatUtcTimestamp(accountSettings.inboundState.lastImportedAtUtc) })
                      : t('emailInboundStateNothingImported')}
                  </span>
                </>
              ) : (
                <span>{t('emailInboundStateNeverPolled')}</span>
              )}
              {accountSettings.inboundState?.lastError ? (
                <span className={styles.panelError}>{t('emailInboundStateLastError', { value: accountSettings.inboundState.lastError })}</span>
              ) : null}
            </div>
          ) : null}
          <div className={`${styles.fieldWide} ${styles.credentialStatus}`}>
            <span>{t('emailImapPassword')}: <ConnectorFieldStatus done={Boolean(accountSettings?.imap.hasPassword)} /></span>
            <span>{t('emailInboundStatus')}: <ConnectorFieldStatus done={Boolean(accountSettings?.isInboundConfigured)} /></span>
          </div>
        </>
      ) : null}

      {settingsForm.inboundMode === 'webhook' ? (
        <>
          {textField('inboundWebhookSecret', t('emailInboundWebhookSecret'), { type: 'password', placeholder: accountSettings?.hasInboundWebhookSecret ? t('emailCredentialConfigured') : '', hint: t('emailInboundWebhookSecretHint'), wide: true })}
          <div className={styles.panel} data-testid="email-inbound-webhook-urls">
            <span className={styles.panelTitle}>{t('emailInboundWebhookUrlsHeading')}</span>
            <span>{t('emailInboundWebhookUrlsHelp')}</span>
            {emailChannelAccounts.selectedAccount ? (
              EMAIL_INBOUND_WEBHOOK_PROVIDERS.map(provider => {
                const url = buildEmailInboundWebhookUrl(provider, emailChannelAccounts.selectedAccount!.id);
                return (
                  <div key={provider} className={styles.copyRow}>
                    <span className={styles.copyRowLabel}>
                      <EmailIcon id={provider === 'raw' ? 'webhook' : provider} size={16} />
                      {' '}
                      {t(`emailInboundWebhookProvider_${provider}`)}
                    </span>
                    <code className={styles.code}>{url}?token=…</code>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => copyToClipboard(url, 'emailInboundWebhookUrlCopied')}
                    >
                      {t('emailInboundWebhookUrlCopy')}
                    </button>
                  </div>
                );
              })
            ) : (
              <span>{t('emailInboundWebhookUrlsAfterSave')}</span>
            )}
          </div>
          <div className={`${styles.fieldWide} ${styles.credentialStatus}`}>
            <span>{t('emailInboundWebhookSecret')}: <ConnectorFieldStatus done={Boolean(accountSettings?.hasInboundWebhookSecret)} /></span>
            <span>{t('emailInboundStatus')}: <ConnectorFieldStatus done={Boolean(accountSettings?.isInboundConfigured)} /></span>
          </div>
        </>
      ) : null}
    </div>
  );

  const behaviourBody = (
    <div className="company-form-grid">
      <Field label={t('emailReplyFormat')}>
        <select
          className="text-input"
          value={settingsForm.replyFormat}
          onChange={event => setSettingsForm(prev => ({ ...prev, replyFormat: event.target.value as EmailReplyFormat }))}
          disabled={!canManage}
        >
          <option value="html">{t('emailReplyFormatHtml')}</option>
          <option value="text">{t('emailReplyFormatText')}</option>
        </select>
      </Field>
      {textField('subjectPrefix', t('emailSubjectPrefix'), { placeholder: 'Re:' })}
      {textField('defaultSubject', t('emailDefaultSubject'), { placeholder: t('emailDefaultSubjectPlaceholder') })}
      {textField('threadIdleDays', t('emailThreadIdleDays'), { type: 'number', placeholder: '30', hint: t('emailThreadIdleDaysHint') })}
      {textField('maxAttachmentSizeMb', t('emailMaxAttachmentSize'), { type: 'number', placeholder: '10' })}
      <Field label={t('emailSignature')} wide>
        <textarea
          className="text-input"
          rows={3}
          value={settingsForm.signature}
          onChange={event => setSettingsForm(prev => ({ ...prev, signature: event.target.value }))}
          placeholder={t('emailSignaturePlaceholder')}
          disabled={!canManage}
        />
      </Field>
      <ToggleField
        label={t('emailUseAgentSignatures')}
        hint={t('emailUseAgentSignaturesHint')}
        checked={settingsForm.useAgentSignatures}
        onChange={update('useAgentSignatures')}
        disabled={!canManage}
      />
      <ToggleField
        label={t('emailQuoteOriginal')}
        checked={settingsForm.quoteOriginal}
        onChange={update('quoteOriginal')}
        disabled={!canManage}
      />
      <ToggleField
        label={t('emailUseBrandLayout')}
        hint={t('emailUseBrandLayoutHint')}
        checked={settingsForm.useBrandLayout}
        onChange={update('useBrandLayout')}
        disabled={!canManage || settingsForm.replyFormat === 'text'}
      />
      <Field label={t('emailLayoutForMailbox')} hint={t('emailLayoutForMailboxHint')}>
        <select
          className="text-input"
          value={settingsForm.layoutId}
          onChange={event => setSettingsForm(prev => ({ ...prev, layoutId: event.target.value }))}
          disabled={!canManage || !settingsForm.useBrandLayout || settingsForm.replyFormat === 'text'}
        >
          <option value="">{t('emailLayoutWorkspaceDefault')}</option>
          {layouts.map(layout => (
            <option key={layout.id} value={layout.id}>{layout.name}</option>
          ))}
          {settingsForm.layoutId && !layouts.some(layout => layout.id === settingsForm.layoutId) ? (
            <option value={settingsForm.layoutId}>{t('emailLayoutMissing')}</option>
          ) : null}
        </select>
      </Field>
    </div>
  );

  const filtersBody = (
    <div className="company-form-grid">
      {textField('allowedSenderDomains', t('emailAllowedSenderDomains'), { placeholder: 'company.com, partner.com', hint: t('emailDomainListHint') })}
      {textField('blockedSenderDomains', t('emailBlockedSenderDomains'), { placeholder: 'spam.example', hint: t('emailDomainListHint') })}
      {textField('ignoreSubjectPattern', t('emailIgnoreSubjectPattern'), { placeholder: '^(Out of Office|Automatic reply)', hint: t('emailIgnoreSubjectPatternHint') })}
      <ToggleField
        label={t('emailIgnoreAutoGenerated')}
        checked={settingsForm.ignoreAutoGenerated}
        onChange={update('ignoreAutoGenerated')}
        disabled={!canManage}
      />
    </div>
  );

  const hasAccount = Boolean(emailChannelAccounts.selectedAccount);
  const testsBody = (
    <div className={styles.testGrid}>
      <div className={styles.testCard}>
        <h4><EmailIcon id="outbound" size={18} />{t('emailTestOutboundCard')}</h4>
        <p className={`${styles.muted} ${styles.small}`}>{t('emailTestOutboundCardHelp')}</p>
        <div className={styles.inlineActions}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { void runTest('outbound'); }}
            disabled={!canManage || testingOutbound || !hasAccount}
          >
            {testingOutbound ? t('savingEllipsis', { ns: 'admin' }) : t('testEmailOutbound')}
          </button>
        </div>
        {renderTestResult(latestOutboundTest)}
        <div className={styles.copyRow}>
          <input
            className="text-input"
            type="email"
            value={testRecipient}
            onChange={event => setTestRecipient(event.target.value)}
            placeholder={t('emailTestRecipientPlaceholder')}
            aria-label={t('emailTestRecipientPlaceholder')}
            disabled={!canManage}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { void sendTestEmail(); }}
            disabled={!canManage || sendingTest || !hasAccount || !testRecipient.trim()}
          >
            {sendingTest ? t('savingEllipsis', { ns: 'admin' }) : t('sendTestEmail')}
          </button>
        </div>
        {latestSendTest ? (
          <p className={`${styles.testResult} ${latestSendTest.succeeded ? styles.testOk : styles.testBad}`}>
            {latestSendTest.succeeded
              ? t('emailTestSendMessageId', { id: latestSendTest.messageId ?? '—' })
              : latestSendTest.failureReason}
          </p>
        ) : null}
      </div>

      <div className={styles.testCard}>
        <h4><EmailIcon id="inbound" size={18} />{t('emailTestInboundCard')}</h4>
        <p className={`${styles.muted} ${styles.small}`}>
          {accountSettings?.inboundMode === 'imap' ? t('emailTestInboundCardHelp') : t('emailTestInboundCardImapOnly')}
        </p>
        <div className={styles.inlineActions}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { void runTest('inbound'); }}
            disabled={!canManage || testingInbound || !hasAccount || accountSettings?.inboundMode !== 'imap'}
          >
            {testingInbound ? t('savingEllipsis', { ns: 'admin' }) : t('testEmailInbound')}
          </button>
        </div>
        {renderTestResult(latestInboundTest)}
      </div>

      <div className={styles.testCard} style={{ gridColumn: '1 / -1' }}>
        <h4><EmailIcon id="dns" size={18} />{t('emailDnsSection')}</h4>
        <p className={`${styles.muted} ${styles.small}`}>{t('emailDnsHelp')}</p>
        <div className={styles.copyRow}>
          <input
            className="text-input"
            type="text"
            value={dkimSelector}
            onChange={event => setDkimSelector(event.target.value)}
            placeholder={t('emailDnsDkimSelectorPlaceholder')}
            aria-label={t('emailDnsDkimSelector')}
            disabled={!canManage}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { void runDnsCheck(); }}
            disabled={!canManage || checkingDns || !hasAccount}
          >
            {checkingDns ? t('savingEllipsis', { ns: 'admin' }) : t('checkEmailDns')}
          </button>
        </div>
        {dnsDiagnostics ? (
          <div className={styles.dnsList} data-testid="email-dns-results">
            <div className={styles.dnsOverall}>
              <StatusPill tone={dnsTone[dnsDiagnostics.overallStatus]}>{t(`emailDnsStatus_${dnsDiagnostics.overallStatus}`)}</StatusPill>
              <span>{t('emailDnsCheckedDomain', { domain: dnsDiagnostics.domain, value: formatUtcTimestamp(dnsDiagnostics.checkedAtUtc) })}</span>
            </div>
            {dnsDiagnostics.checks.map(check => (
              <div key={check.id} className={styles.dnsCheck}>
                <div className={styles.dnsCheckHead}>
                  <StatusPill tone={dnsTone[check.status]}>{t(`emailDnsStatus_${check.status}`)}</StatusPill>
                  <span className={styles.dnsCheckId}>{check.id.toUpperCase()}</span>
                  <span>{check.summary}</span>
                </div>
                {check.detail ? <span className={styles.dnsDetail}>{check.detail}</span> : null}
                {check.records.map(record => <pre key={record} className={styles.dnsRecord}>{record}</pre>)}
                {check.recommendation ? <span className={styles.dnsFix}>{t('emailDnsRecommendation', { value: check.recommendation })}</span> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <section className="company-card">
      <ChannelAccountBar
        channel="Email"
        accounts={emailChannelAccounts.accounts}
        limits={emailChannelAccounts.limits}
        loading={emailChannelAccounts.loading}
        error={emailChannelAccounts.error}
        selectedAccountId={emailChannelAccounts.selectedAccountId}
        onSelectAccount={emailChannelAccounts.setSelectedAccountId}
        onRefresh={() => { void emailChannelAccounts.reload(); }}
        canManage={canManage}
        showAddForm={showAddAccount}
        onToggleAddForm={() => setShowAddAccount(current => !current)}
        onAccountUpdated={() => { void emailChannelAccounts.reload(); }}
      />

      {showAddAccount && canManage ? (
        <p className={styles.disabledNote}>{t('channelAccountAddEmailHelp')}</p>
      ) : null}

      {emailChannelAccounts.loading && emailChannelAccounts.accounts.length === 0 ? (
        <Spinner />
      ) : (
        <>
          <div className={styles.intro}>
            <h3>{t('emailIntegrationHeading')}</h3>
            <p className={styles.muted}>{t('emailIntegrationHelp')}</p>
          </div>

          <EmailBrandingPanel canManage={canManage} onLayoutsChanged={refreshLayouts} />

          {!isInitialSetup ? (
            <div className={styles.summary} data-testid="email-summary">
              {summaryItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={styles.summaryItem}
                  onClick={() => revealSection(item.id)}
                  title={t('emailSummaryOpenSection')}
                >
                  <span className={styles.summaryIcon}><EmailIcon id={item.icon} size={20} /></span>
                  <span className={styles.summaryBody}>
                    <span className={styles.summaryLabel}>{item.label}</span>
                    <span className={styles.summaryValue}>
                      <span className={`${styles.summaryDot} ${dotClass[item.tone]}`} aria-hidden="true" />
                      {item.value}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {settingsError ? <div className="error">{settingsError}</div> : null}
          {settingsNotice ? <div className="success">{settingsNotice}</div> : null}

          {loadingSettings ? <Spinner /> : (
            <>
              <div className={styles.sectionTools}>
                <button type="button" className={styles.linkButton} onClick={() => setAllSections(true)}>{t('emailExpandAll')}</button>
                <button type="button" className={styles.linkButton} onClick={() => setAllSections(false)}>{t('emailCollapseAll')}</button>
              </div>

              {renderSection(
                'identity',
                t('emailIdentitySection'),
                t('emailIdentityHelp'),
                <StatusPill tone={settingsForm.address.trim() ? 'ok' : 'bad'}>
                  {settingsForm.address.trim() || t('missing')}
                </StatusPill>,
                identityBody,
              )}
              {renderSection(
                'outbound',
                t('emailOutboundSection'),
                t('emailOutboundHelp'),
                <StatusPill tone={outboundTone}>
                  {!settingsForm.outboundEnabled ? t('disabled') : `${outboundLabel} · ${accountSettings?.isOutboundConfigured ? t('configured') : t('missing')}`}
                </StatusPill>,
                outboundBody,
              )}
              {showOAuthSection ? renderSection(
                'oauth',
                t('emailOAuthSection'),
                t('emailOAuthHelp'),
                <StatusPill tone={oauthTone}>{oauthLabel}</StatusPill>,
                oauthBody,
              ) : null}
              {renderSection(
                'inbound',
                t('emailInboundSection'),
                t('emailInboundHelp'),
                <StatusPill tone={inboundTone}>
                  {settingsForm.inboundMode === 'none'
                    ? t('emailSummaryOff')
                    : `${inboundLabel} · ${!settingsForm.inboundEnabled ? t('disabled') : accountSettings?.isInboundConfigured ? t('configured') : t('missing')}`}
                </StatusPill>,
                inboundBody,
              )}
              {renderSection(
                'behaviour',
                t('emailBehaviourSection'),
                t('emailBehaviourHelp'),
                <StatusPill tone="off">{settingsForm.replyFormat === 'html' ? t('emailReplyFormatHtml') : t('emailReplyFormatText')}</StatusPill>,
                behaviourBody,
              )}
              {renderSection(
                'filters',
                t('emailFiltersSection'),
                t('emailFiltersHelp'),
                <StatusPill tone={splitDomains(settingsForm.allowedSenderDomains).length + splitDomains(settingsForm.blockedSenderDomains).length > 0 || settingsForm.ignoreSubjectPattern.trim() ? 'ok' : 'off'}>
                  {t('emailFiltersPill', { count: splitDomains(settingsForm.allowedSenderDomains).length + splitDomains(settingsForm.blockedSenderDomains).length + (settingsForm.ignoreSubjectPattern.trim() ? 1 : 0) + (settingsForm.ignoreAutoGenerated ? 1 : 0) })}
                </StatusPill>,
                filtersBody,
              )}
              {!isInitialSetup ? renderSection(
                'tests',
                t('emailTestsSection'),
                t('emailTestsHelp'),
                <StatusPill tone={dnsTone_}>{dnsLabel}</StatusPill>,
                testsBody,
              ) : null}
            </>
          )}

          {canManage ? (
            <div className={styles.footer}>
              <span className={`${styles.footerNote} ${isDirty ? styles.footerDirty : ''}`}>
                {isDirty ? t('emailUnsavedChanges') : t('emailAllSaved')}
              </span>
              <div className={styles.footerActions}>
                {isDirty && !isInitialSetup ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSettingsForm(baselineForm)}
                    disabled={savingSettings}
                  >
                    {t('emailDiscardChanges')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { void saveSettings(); }}
                  disabled={savingSettings || loadingSettings}
                >
                  {savingSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveEmailSettings')}
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
